import { CancellationPolicy, Experience, ExperienceStatus, Order } from '@/types'
import React, { useMemo } from 'react'
import ExperienceMedia from './experience-media'
import ExperienceFromPrice from './experience-from-price'
import { TFunction, useTranslation } from 'next-i18next'
import ExperienceHighlight from './experience-highlight'
import ExperienceRate from '@/components/experiences/experience-rate'
import ExperienceBooking from '@/components/app/experiences/experience-booking'
import {
  AlertTriangle,
  BellRing,
  Calendar,
  Car,
  Check,
  Clock,
  Lock,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { trim } from 'lodash-es'
import { sanitize } from '@/lib/sanitize'
import { prepareHtml } from '@/lib/html-utils'
import ExperienceContacts from '@/components/experiences/experience-contacts'
import ExperienceAddressCard from './experience-address-card'
import { useRouter } from 'next/navigation'
import { useRouter as usePagesRouter } from 'next/router'
import { isFreeExperience } from '@/helper/utils'
import { getLocalizedText } from '@/lib/constants/locales'

const getExperienceLocation = (experience: Experience, locale: string) => {
  if (experience.location?.city) {
    return `${experience.location.city}, ${experience.location.country}`
  }

  if (experience.location?.formattedAddress) {
    return experience.location.formattedAddress
  }

  return getLocalizedText(experience.location?.name, locale) ?? ''
}

// DEV-84 — the experience-level `meetingPoint` column is legacy data left over
// from before commit 1393ab18, which moved meeting points to the option level.
// The form no longer edits it but stale values persist on existing experiences
// and would otherwise mask `experience.location` here. We intentionally render
// only `experience.location` plus pickup-point info.
const WhereSection = ({
  experience,
  t,
}: {
  experience: Experience
  t: TFunction
}) => {
  if (!experience.pickingPoints?.length && !experience.allowCustomPickingPoint) {
    return <ExperienceAddressCard address={experience.location} t={t} />
  }

  return (
    <ul className="">
      <li>
        <ExperienceAddressCard address={experience.location} t={t} />
      </li>

      {experience.pickingPoints?.map((pickupoint, i) => (
        <li key={i}>
          <ExperienceAddressCard address={pickupoint} t={t} />
        </li>
      ))}

      {experience.allowCustomPickingPoint ? (
        <li>{t('arrange_a_pickup')}</li>
      ) : null}
    </ul>
  )
}

const ExperienceDetail = ({
  experience,
  bookable = true,
  sellerId,
  widgetUuid,
  voucherOrder,
  onVoucherRedeemed,
}: {
  experience: Experience
  bookable?: boolean
  sellerId: number
  widgetUuid?: string
  /**
   * When set, the embedded ExperienceBooking renders in voucher-redemption
   * mode (no total, no price labels) and the "starting from" header price
   * is hidden — the guest already paid via the bundle, showing prices is
   * confusing. Used by /redeem/voucher/[code].
   */
  voucherOrder?: Order
  /** Called after a successful voucher redemption — typically navigates
   *  back to the originating bundle landing. */
  onVoucherRedeemed?: () => void
}) => {
  const { t } = useTranslation('experience')
  const router = useRouter()
  const pagesRouter = usePagesRouter()
  const locale = pagesRouter.locale ?? 'en'

  const duration = useMemo(() => {
    const times = []
    const option = experience.options[0]

    if (!option) {
      return null
    }

    if (option.days > 0) {
      times.push(t('days', { count: option.days }))
    }

    if (option.hours > 0) {
      times.push(t('hours', { count: option.hours }))
    }

    if (option.minutes > 0) {
      times.push(t('minutes', { count: option.minutes }))
    }

    return times.join(', ')
  }, [experience.options, t])

  const isFree = isFreeExperience(experience)

  // Check if experience is unavailable (for warning banner)
  const isExperienceUnavailable = experience.status !== ExperienceStatus.Active

  return (
    <div>
      <h1 className="text-3xl mt-4">{experience.name}</h1>
      <div className="text-md mb-6 mt-2">
        {getExperienceLocation(experience, locale)}
      </div>

      {/* <div className="my-6 flex items-center space-x-2">
      <ExperienceRate experience={experience} />{' '}
      <a className="text-sm underline" href="#reviews">
        {t('reviews', { count: experience.rateCount })}
      </a>
    </div> */}

      {experience.images.length > 0 && (
        <ExperienceMedia experience={experience} />
      )}

      <section className="flex pb-4 space-x-4 w-full justify-between">
        <div className="flex-1">
          {!voucherOrder && (
            <div className="mt-4">
              <ExperienceFromPrice experience={experience} />
            </div>
          )}

          {isExperienceUnavailable && (
            <div className="mt-4 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                {t('experience_not_available_for_booking')}
              </div>
            </div>
          )}

          <h2 className="text-xl font-semibold mt-6 mb-2">{t('overview')}</h2>

          {experience.highlight && (
            <div
              className="my-4 border-t pt-4 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: sanitize(prepareHtml(experience.highlight)),
              }}
            />
          )}

          <ul className="border-b border-t my-8 py-4">
            {duration ? (
              <ExperienceHighlight title={duration} icon={Clock} />
            ) : null}

            {!!experience.allowCustomPickingPoint && (
              <ExperienceHighlight
                title={t('highlight.pickup')}
                content={t('highlight.pickup_description')}
                icon={Car}
              />
            )}

            {!!experience.options.find((option) => option.isPrivate) && (
              <ExperienceHighlight
                title={t('highlight.private')}
                content={t('highlight.private_description')}
                icon={Lock}
              />
            )}

            {!isFree &&
              (experience.cancellationPolicy !==
              CancellationPolicy.NON_REFUNDABLE ? (
                <ExperienceHighlight
                  title={t('highlight.free_cancellation')}
                  content={t('highlight.free_cancellation_description', {
                    duration: t(experience.cancellationPolicy),
                  })}
                  icon={Calendar}
                />
              ) : (
                <ExperienceHighlight
                  title={t('highlight.non_refundable_cancellation')}
                  content={''}
                  icon={Calendar}
                />
              ))}
          </ul>

          <div className="border-b pb-6">
            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">
                {t('discover_the_details')}
              </h2>

              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitize(prepareHtml(experience.description)),
                }}
              />
            </div>
          </div>

          {(experience.inclusions.length > 0 || experience.exclusions.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 border-b pb-8">
              <div>
                <h2 className="text-lg font-semibold mt-6 mb-2">
                  {t('whats_included?')}
                </h2>

                <ul className="space-y-2">
                  {experience.inclusions.map((inclusion, i) => (
                    <li key={i} className="flex items-center space-x-2">
                      <span>
                        <Check />
                      </span>
                      <span className="first-letter:uppercase"> {inclusion}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {experience.exclusions.length > 0 ? (
                <div>
                  <h2 className="text-lg font-semibold mt-6 mb-2">
                    {t('whats_not_included?')}
                  </h2>

                  <ul className="space-y-2">
                    {experience.exclusions.map((exclusion, i) => (
                      <li key={i} className="flex items-center space-x-2">
                        <span>
                          <X />
                        </span>
                        <span className="first-letter:uppercase">
                          {exclusion}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}

          {/* DEV-460 — pet access is a booking-context preference and does
              not apply to information pages. */}
          {experience.petAccess !== null && !experience.isInformationPage && (
            <div className="border-b pb-8">
              <h2 className="text-lg font-semibold mt-6 mb-2">
                {t('pet_allowed?')}
              </h2>

              <div className="flex items-center space-x-2">
                {experience.petAccess ? (
                  <>
                    <Check /> <span>{t('yes')}</span>
                  </>
                ) : (
                  <>
                    <X /> <span>{t('no')}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* DEV-784 — accessibility is descriptive content (venue access
              info) and shows whenever filled, including on information pages. */}
          {experience.accessibility && (
            <div className="border-b pb-8">
              <h2 className="text-lg font-semibold mt-6 mb-2">
                {t('accessibility')}
              </h2>

              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitize(prepareHtml(experience.accessibility || '')),
                }}
              />
            </div>
          )}

          {experience.notes && (
            <div className="border-b pb-8">
              <h2 className="text-lg font-semibold mt-6 mb-2">{t('notes')}</h2>

              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitize(prepareHtml(experience.notes || '')),
                }}
              />
            </div>
          )}

          <div className="border-b pb-6">
            <h2 className="text-lg font-semibold mt-6 mb-2">{t('where')}</h2>

            <div>
              <WhereSection experience={experience} t={t} />
            </div>
          </div>

          <ExperienceContacts experience={experience} />
        </div>

        <div className="mt-6 hidden md:block flex-1 max-w-[424px]">
          <ExperienceBooking
            voucherOrder={voucherOrder}
            skipPostSubmitRedirect={Boolean(voucherOrder)}
            afterSubmit={voucherOrder ? onVoucherRedeemed : undefined}
            experience={experience}
            bookable={bookable}
            sellerId={sellerId}
            widgetUuid={widgetUuid}
            redirectToCheckout={() => {
              router.push(`/app/checkout`)
            }}
          />
        </div>
      </section>
    </div>
  )
}

export default ExperienceDetail
